/** @jsx jsx */
import { Fragment, h } from 'preact';

// Searchspring
import { useTheme, CacheProvider } from '@searchspring/snap-preact-components/dist/cjs/providers'
import { defined } from '@searchspring/snap-preact-components/dist/cjs/utilities'

// Packages
import { jsx, css } from '@emotion/react'
import { observer } from 'mobx-react-lite'

// Local 
import { fetchLocaleField } from '../Helpers';

// New Cards
import { StandardProductCard } from '../Cards/StandardProductCard/StandardProductCard';
import { FixedBundleProductCard } from '../Cards/FixedBundleProductCard/FixedBundleProductCard';
import { CustomBundleProductCard } from '../Cards/CustomBundleProductCard/CustomBundleProductCard';
import { PersonalizedProductCard } from '../Cards/PersonalizedProductCard/PersonalizedProductCard';
import { CartUpsellProductCard } from '../Cards/CartUpsellProductCard/CartUpsellProductCard';
import { ATCWithVariantSelectProductCard } from '../Cards/ATCWithVariantSelectProductCard/ATCWithVariantSelectProductCard';
import { TypeAheadStandardProductCard } from '../Cards/TypeAheadStandardProductCard/TypeAheadStandardProductCard';
import { TypeAheadFixedBundleProductCard } from '../Cards/TypeAheadFixedBundleProductCard/TypeAheadFixedBundleProductCard';
import { TypeAheadCustomBundleProductCard } from '../Cards/TypeAheadCustomBundleProductCard/TypeAheadCustomBundleProductCard';

// CSS
import './result.css'

const CSS = {
    result: () =>
        css({
            '&.ss__result--grid': {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                '& .ss__result__image-wrapper': {
                    flex: '1 0 auto',
                },
            },
            '&.ss__result--list': {
                display: 'flex',
                flexDirection: 'row',
                '& .ss__result__image-wrapper': {
                    flex: '0 0 33%',
                },
                '& .ss__result__details': {
                    flex: '1 1 auto',
                    textAlign: 'left',
                    marginLeft: '20px',
                    padding: 0,
                },
            },

            '& .ss__result__image-wrapper': {
                position: 'relative',
                '& .ss__result__badge': {
                    background: '#FFFFFF',
                },
            },

            '& .ss__result__details': {
                textAlign: 'center',

                '& .ss__result__details__title': {
                    marginBottom: '10px',
                },
                '& .ss__result__details__pricing': {
                    marginBottom: '10px',

                    '& .ss__result__price': {
                        fontSize: '1.2em',
                    },
                    '& .ss__price--strike': {
                        fontSize: '80%',
                    },
                },
                '& .ss__result__details__button': {
                    marginBottom: '10px',
                },
            },
        }),
}

export const PRODUCT_CARD_TYPES = {
    STANDARD_PRODUCT_CARD: 'StandardProductCard',
    FIXED_BUNDLE_PRODUCT_CARD: 'FixedBundleProductCard',
    CUSTOM_BUNDLE_PRODUCT_CARD: 'CustomBundleProductCard',
    PERSONALIZED_PRODUCT_CARD: 'PersonalizedProductCard',
    CART_UPSELL_PRODUCT_CARD: 'CartUpsellProductCard',
    ATC_WITH_VARIANT_SELECT_PRODUCT_CARD: 'ATCWithVariantSelectProductCard',
    TYPE_AHEAD_STANDARD_PRODUCT_CARD: 'TypeAheadStandardProductCard',
    TYPE_AHEAD_FIXED_BUNDLE_PRODUCT_CARD: 'TypeAheadFixedBundleProductCard',
    TYPE_AHEAD_CUSTOM_BUNDLE_PRODUCT_CARD: 'TypeAheadCustomBundleProductCard',
}

export const Result = observer(properties => {
    const globalTheme = useTheme();

    // Get props from theme/properties param
    const props = {
        layout: 'grid',
        ...globalTheme?.components?.result,
        ...properties,
        ...properties.theme?.components?.result,
    }

    // Destructure relevant props from props obj
    const { result, fallback, disableStyles, style, cardType } = props;

    const core = result?.mappings?.core;
    const badge = fetchLocaleField(result, "mfield_cql_badge", "mfield_french_badge");

    const styling = {};
    if (!disableStyles) {
        styling.css = [CSS.result(), style];
    } else if (style) {
        styling.css = [style];
    }

    // Get subProps based on above variables
    const subProps = {
        price: {
            className: 'ss__result__price',
            ...globalTheme?.components?.price,
            ...defined({
                disableStyles,
            }),
            theme: props.theme,
        },
        badge: {
            className: 'ss__result__badge',
            content: badge,
            ...globalTheme?.components?.badge,
            ...defined({
                disableStyles,
            }),
            theme: props.theme,
        },
        image: {
            className: 'ss__result__image',
            alt: core?.name,
            src: core?.imageUrl,
            ...globalTheme?.components?.image,
            ...defined({
                disableStyles,
                fallback: fallback,
            }),
            theme: props.theme,
        },
    }

    // Props to pass down to various types of product cards
    const cardProps = {
        parentProps: props,
        parentSubProps: subProps,
        styling,
    }

    function ResultCard ({ cardType }) {
        switch (cardType) {
            case PRODUCT_CARD_TYPES.STANDARD_PRODUCT_CARD:
                return <StandardProductCard cardProps={cardProps} />;
            case PRODUCT_CARD_TYPES.FIXED_BUNDLE_PRODUCT_CARD:
                return <FixedBundleProductCard cardProps={cardProps} />;   
            case PRODUCT_CARD_TYPES.CUSTOM_BUNDLE_PRODUCT_CARD:
                return <CustomBundleProductCard cardProps={cardProps} />;
            case PRODUCT_CARD_TYPES.PERSONALIZED_PRODUCT_CARD:
                return <PersonalizedProductCard cardProps={cardProps} />;
            case PRODUCT_CARD_TYPES.CART_UPSELL_PRODUCT_CARD:
                return <CartUpsellProductCard cardProps={cardProps} />;   
            case PRODUCT_CARD_TYPES.ATC_WITH_VARIANT_SELECT_PRODUCT_CARD:
                return <ATCWithVariantSelectProductCard cardProps={cardProps} />;   
            case PRODUCT_CARD_TYPES.TYPE_AHEAD_STANDARD_PRODUCT_CARD:
                return <TypeAheadStandardProductCard cardProps={cardProps} />;               
            case PRODUCT_CARD_TYPES.TYPE_AHEAD_FIXED_BUNDLE_PRODUCT_CARD:
                return <TypeAheadFixedBundleProductCard cardProps={cardProps} />;      
            case PRODUCT_CARD_TYPES.TYPE_AHEAD_CUSTOM_BUNDLE_PRODUCT_CARD:
                return <TypeAheadCustomBundleProductCard cardProps={cardProps} />;     
            default:
                return <StandardProductCard cardProps={cardProps} />;
        }
    }

    return (
        <CacheProvider>
            <ResultCard cardType={cardType} />
        </CacheProvider>
    )
})
